import { useConfirmModal } from '@affine/component';
import {
  UserSettingsService,
  workspaceApiFetch,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { confirmByModal } from '@affine/core/utils/confirm-by-modal';
import { useI18n } from '@affine/i18n';
import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { useLiveData, useService } from '@toeverything/infra';
import { type MouseEvent, useCallback, useEffect, useState } from 'react';

import type { AttachmentViewerSource } from '../types';
import { buildAttachmentSource } from '../utils';
import { createPdfSearchCoverageAttachmentBlockId } from './pdf-index-review';
import {
  imageTextProgress,
  mapCoverageStatus,
  type PdfCoverageResponse,
  requiresPdfOcrConfirmation,
  shouldOpenPdfSearchCoverageCoachmark,
} from './pdf-search-coverage';
import type { PdfSearchCoverageBadgeState } from './pdf-search-coverage-badge';

type PdfOcrPolicy =
  | 'manual'
  | 'auto_under_limit'
  | 'always_auto_within_limits'
  | 'disabled_visible'
  | 'disabled_hidden';

class PdfCoverageHttpError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

function isPdfCoverageUnavailable(error: unknown): boolean {
  return (
    error instanceof TypeError ||
    (error instanceof PdfCoverageHttpError && error.status >= 500)
  );
}

export function usePdfSearchCoverage(
  input:
    | {
        source: AttachmentViewerSource;
      }
    | {
        model: AttachmentBlockModel;
      }
) {
  const source =
    'source' in input ? input.source : buildAttachmentSource(input.model);
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();
  const workspace = useService(WorkspaceService).workspace;
  const workspaceServer = useService(WorkspaceServerService);
  const userSettingsService = useService(UserSettingsService);
  const userSettings = useLiveData(userSettingsService.userSettings$);
  const serverBaseUrl = workspaceServer.server?.baseUrl ?? location.origin;
  const [coverageState, setCoverageState] =
    useState<PdfSearchCoverageBadgeState>({
      availability: 'loading',
      textIndexed: false,
      textIndexStatus: 'idle',
      imageTextStatus: 'manual',
    });
  const [textIndexingAllowed, setTextIndexingAllowed] = useState(false);
  const [coverageLoaded, setCoverageLoaded] = useState(false);
  const [pdfOcrPolicy, setPdfOcrPolicy] = useState<PdfOcrPolicy>(
    'always_auto_within_limits'
  );
  const [coverageCandidatePageCount, setCoverageCandidatePageCount] =
    useState(0);
  const [confirmationThresholdPages, setConfirmationThresholdPages] =
    useState(12);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const isSearchIndexSupported = source?.supportsPdfSearchIndex === true;
  const attachmentBlockId = source
    ? createPdfSearchCoverageAttachmentBlockId(source)
    : null;
  const docId = source?.docId ?? null;

  useEffect(() => {
    if (!isSearchIndexSupported) return;
    userSettingsService.revalidate();
  }, [isSearchIndexSupported, userSettingsService]);

  useEffect(() => {
    if (!isSearchIndexSupported) {
      setCoverageLoaded(true);
      return;
    }
    let cancelled = false;
    const loadPolicy = async () => {
      try {
        const res = await workspaceApiFetch(
          `${serverBaseUrl}/api/workspace/${workspace.id}/ai-defaults`,
          { credentials: 'include' }
        );
        if (!res.ok) return;
        const json = await res.json();
        const policy = json.defaults?.pdfOcrPolicy;
        if (!cancelled && isPdfOcrPolicy(policy)) {
          setPdfOcrPolicy(policy);
          const threshold = Number(
            json.defaults?.pdfOcrConfirmationThresholdPages
          );
          if (Number.isInteger(threshold) && threshold > 0) {
            setConfirmationThresholdPages(threshold);
          }
          if (policy === 'disabled_visible' || policy === 'disabled_hidden') {
            setCoverageState(prev => ({
              ...prev,
              imageTextStatus: 'disabled',
            }));
          }
        }
      } catch (error) {
        console.error('PDF OCR policy load failed', error);
      }
    };
    void loadPolicy();
    return () => {
      cancelled = true;
    };
  }, [isSearchIndexSupported, serverBaseUrl, workspace.id]);

  const refreshCoverage = useCallback(async () => {
    if (!isSearchIndexSupported) return null;
    if (!docId || !attachmentBlockId) return null;
    const url = new URL(
      `${serverBaseUrl}/api/workspace/${workspace.id}/pdf-search-coverage`
    );
    url.searchParams.set('docId', docId);
    url.searchParams.set('attachmentBlockId', attachmentBlockId);
    const res = await workspaceApiFetch(url, { credentials: 'include' });
    if (!res.ok) {
      throw new PdfCoverageHttpError(
        `PDF OCR coverage failed: ${res.status}`,
        res.status
      );
    }
    return (await res.json()) as PdfCoverageResponse;
  }, [
    attachmentBlockId,
    docId,
    isSearchIndexSupported,
    serverBaseUrl,
    workspace.id,
  ]);

  const applyCoverage = useCallback(
    (coverage: PdfCoverageResponse) => {
      setCoverageCandidatePageCount(
        (coverage.skippedPageCount ?? 0) > 0
          ? (coverage.skippedPageCount ?? 0)
          : (coverage.candidatePageCount ?? 0)
      );
      setTextIndexingAllowed(coverage.textIndexingAllowed === true);
      setCoverageState(prev => {
        const imageTextStatus =
          pdfOcrPolicy === 'disabled_visible' ||
          pdfOcrPolicy === 'disabled_hidden' ||
          coverage.managedCreditsAvailable === false
            ? 'disabled'
            : mapCoverageStatus(coverage);
        return {
          ...prev,
          availability: 'available',
          textIndexed: coverage.textIndexed === true,
          indexedPages: coverage.indexedPages,
          imageTextProgress: imageTextProgress(coverage),
          pageLimit: coverage.pageLimit,
          imageTextFileLimitMb: coverage.imageTextFileLimitMb,
          aiIndexed: coverage.aiIndexed,
          aiCreditsAvailable: coverage.managedCreditsAvailable,
          textIndexStatus:
            coverage.textIndexed === true
              ? 'idle'
              : prev.textIndexStatus === 'pending'
                ? 'pending'
                : prev.textIndexStatus,
          imageTextStatus:
            prev.imageTextStatus === 'pending' && imageTextStatus === 'manual'
              ? 'pending'
              : imageTextStatus,
        };
      });
    },
    [pdfOcrPolicy]
  );

  useEffect(() => {
    let cancelled = false;
    setCoverageLoaded(false);
    void refreshCoverage()
      .then(coverage => {
        if (!cancelled && coverage) {
          applyCoverage(coverage);
          setCoverageLoaded(true);
        }
      })
      .catch(error => {
        console.error('PDF OCR coverage load failed', error);
        if (!cancelled) {
          setCoverageState(prev => ({
            ...prev,
            availability: 'unavailable',
            textIndexStatus: 'idle',
            imageTextStatus:
              prev.imageTextStatus === 'pending'
                ? 'manual'
                : prev.imageTextStatus,
          }));
          setCoverageLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [applyCoverage, refreshCoverage]);

  useEffect(() => {
    if (
      coverageState.imageTextStatus !== 'pending' &&
      coverageState.textIndexStatus !== 'pending'
    ) {
      return;
    }
    let cancelled = false;
    const interval = window.setInterval(() => {
      void refreshCoverage()
        .then(coverage => {
          if (!cancelled && coverage) applyCoverage(coverage);
        })
        .catch(error => {
          console.error('PDF OCR coverage poll failed', error);
          if (!cancelled) {
            setCoverageState(prev => ({
              ...prev,
              availability: 'unavailable',
              textIndexStatus:
                prev.textIndexStatus === 'pending'
                  ? 'idle'
                  : prev.textIndexStatus,
              imageTextStatus:
                prev.imageTextStatus === 'pending'
                  ? 'manual'
                  : prev.imageTextStatus,
            }));
          }
        });
    }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    applyCoverage,
    coverageState.imageTextStatus,
    coverageState.textIndexStatus,
    refreshCoverage,
  ]);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const openContextMenu = useCallback(
    (event: MouseEvent) => {
      if (!isSearchIndexSupported) return;
      event.preventDefault();
      event.stopPropagation();
      if (pdfOcrPolicy === 'disabled_hidden') return;
      setContextMenu({ x: event.clientX, y: event.clientY });
    },
    [isSearchIndexSupported, pdfOcrPolicy]
  );

  const indexPdfText = useCallback(async () => {
    if (!isSearchIndexSupported || !docId || !attachmentBlockId) return;
    if (
      !textIndexingAllowed ||
      coverageState.textIndexed ||
      coverageState.textIndexStatus === 'pending'
    ) {
      return;
    }
    setContextMenu(null);
    setCoverageState(prev => ({
      ...prev,
      availability: 'available',
      textIndexStatus: 'pending',
    }));
    try {
      const res = await workspaceApiFetch(
        `${serverBaseUrl}/api/workspace/${workspace.id}/pdf-search-coverage/index`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            docId,
            attachmentBlockId,
          }),
        }
      );
      if (!res.ok) {
        throw new PdfCoverageHttpError(
          `PDF text index request failed: ${res.status}`,
          res.status
        );
      }
      const json = await res.json();
      if (json.queued !== true) {
        throw new Error('PDF text index request failed');
      }
      const coverage = await refreshCoverage();
      if (coverage) applyCoverage(coverage);
    } catch (error) {
      console.error('PDF text indexing failed', error);
      const unavailable = isPdfCoverageUnavailable(error);
      setCoverageState(prev => ({
        ...prev,
        availability: unavailable ? 'unavailable' : 'available',
        textIndexStatus: unavailable ? 'idle' : 'failed',
      }));
    }
  }, [
    applyCoverage,
    coverageState.textIndexStatus,
    coverageState.textIndexed,
    isSearchIndexSupported,
    attachmentBlockId,
    docId,
    refreshCoverage,
    serverBaseUrl,
    textIndexingAllowed,
    workspace.id,
  ]);

  const scanImageText = useCallback(async () => {
    if (!isSearchIndexSupported || !docId || !attachmentBlockId) return;
    if (
      pdfOcrPolicy === 'disabled_visible' ||
      pdfOcrPolicy === 'disabled_hidden' ||
      coverageState.imageTextStatus === 'disabled' ||
      coverageState.imageTextStatus === 'pending'
    ) {
      return;
    }
    setContextMenu(null);
    const requiresConfirmation = requiresPdfOcrConfirmation(
      coverageCandidatePageCount,
      confirmationThresholdPages
    );
    const confirmed =
      !requiresConfirmation ||
      (await confirmByModal({
        openConfirmModal,
        title: t['com.streamlain.pdf.searchCoverage.confirmLargeScan.title'](),
        description: t['com.streamlain.pdf.searchCoverage.confirmLargeScan']({
          count: String(coverageCandidatePageCount),
          threshold: String(confirmationThresholdPages),
        }),
        confirmText: t['Confirm'](),
        cancelText: t['Cancel'](),
      }));
    if (!confirmed) return;

    setCoverageState(prev => ({
      ...prev,
      availability: 'available',
      imageTextStatus: 'pending',
    }));
    try {
      const res = await workspaceApiFetch(
        `${serverBaseUrl}/api/workspace/${workspace.id}/pdf-search-coverage/reindex`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            docId,
            attachmentBlockId,
            confirmed: requiresConfirmation,
          }),
        }
      );
      if (!res.ok) {
        throw new PdfCoverageHttpError(
          `PDF OCR request failed: ${res.status}`,
          res.status
        );
      }
      const json = await res.json();
      if (json.queued !== true) throw new Error('PDF OCR request failed');
      const coverage = await refreshCoverage();
      if (coverage) applyCoverage(coverage);
    } catch (error) {
      console.error('PDF image text OCR failed', error);
      const unavailable = isPdfCoverageUnavailable(error);
      setCoverageState(prev => ({
        ...prev,
        availability: unavailable ? 'unavailable' : 'available',
        imageTextStatus: unavailable ? 'manual' : 'failed',
      }));
    }
  }, [
    confirmationThresholdPages,
    coverageCandidatePageCount,
    coverageState.imageTextStatus,
    attachmentBlockId,
    docId,
    isSearchIndexSupported,
    openConfirmModal,
    pdfOcrPolicy,
    refreshCoverage,
    serverBaseUrl,
    t,
    workspace.id,
    applyCoverage,
  ]);

  const dismissCoachmarkPermanently = useCallback(async () => {
    await userSettingsService.updateUserSettings({
      pdfSearchCoverageCoachmarkDismissed: true,
    });
  }, [userSettingsService]);

  const coachmarkDismissed =
    userSettings?.pdfSearchCoverageCoachmarkDismissed === true;
  const canEvaluateCoachmark = userSettings !== undefined;
  const retryCoverageCheck = useCallback(() => {
    setCoverageState(prev => ({ ...prev, availability: 'loading' }));
    void refreshCoverage()
      .then(coverage => {
        if (coverage) applyCoverage(coverage);
      })
      .catch(error => {
        console.error('PDF OCR coverage retry failed', error);
        setCoverageState(prev => ({ ...prev, availability: 'unavailable' }));
      });
  }, [applyCoverage, refreshCoverage]);

  return {
    coverageState,
    contextMenu,
    closeContextMenu,
    openContextMenu,
    indexPdfText,
    scanImageText,
    retryCoverageCheck,
    coachmarkDismissed,
    dismissCoachmarkPermanently,
    showCoverageCoachmark:
      canEvaluateCoachmark &&
      coverageLoaded &&
      shouldOpenPdfSearchCoverageCoachmark(
        coverageState,
        coverageCandidatePageCount
      ),
    isHidden:
      !isSearchIndexSupported ||
      (pdfOcrPolicy === 'disabled_hidden' && coverageState.textIndexed),
    isDisabled:
      !isSearchIndexSupported ||
      pdfOcrPolicy === 'disabled_visible' ||
      coverageState.imageTextStatus === 'disabled',
    canIndexPdfText:
      textIndexingAllowed &&
      !coverageState.textIndexed &&
      coverageState.textIndexStatus !== 'pending',
    isContextMenuItemDisabled:
      pdfOcrPolicy === 'disabled_visible' ||
      coverageState.imageTextStatus === 'disabled' ||
      coverageState.imageTextStatus === 'pending',
  };
}

function isPdfOcrPolicy(value: unknown): value is PdfOcrPolicy {
  return (
    value === 'manual' ||
    value === 'auto_under_limit' ||
    value === 'always_auto_within_limits' ||
    value === 'disabled_visible' ||
    value === 'disabled_hidden'
  );
}
